import { useEffect, useRef } from "react";
import { useSetAtom } from "jotai";
import { generateModalId, modalStackAtom } from "../atoms/modal";
import type { ModalProps } from "./Modal.types";

// Web implementation of Modal (native one is in Modal.tsx). Headless registrar: pushes its children
// into `modalStackAtom` and lets the single root `ModalPortal` render them as a centered dialog (or a
// full-height slide-up on narrow web). See components/ModalPortal.tsx.
const Modal = ({
    children,
    visible = false,
    hideContainerBackground = false,
    onClose,
    hideCloseButton = false,
    fitContent = false,
    bgColorName,
    edges = {},
    onRequestClose,
}: ModalProps) => {
    const setModalStack = useSetAtom(modalStackAtom);
    const modalIdRef = useRef<string>(generateModalId());

    const finalOnClose = onClose ?? onRequestClose;

    // Effect for visibility changes
    useEffect(() => {
        const modalId = modalIdRef.current;

        if (visible) {
            setModalStack((prev) => {
                const existing = prev.find((m) => m.id === modalId);
                if (existing) {
                    return prev.map((m) =>
                        m.id === modalId
                            ? {
                                  ...m,
                                  visible: true,
                                  children,
                                  onClose: finalOnClose,
                                  hideContainerBackground,
                                  hideCloseButton,
                                  fitContent,
                                  bgColorName,
                                  edges,
                              }
                            : m,
                    );
                }
                return [
                    ...prev,
                    {
                        id: modalId,
                        visible: true,
                        children,
                        onClose: finalOnClose,
                        hideContainerBackground,
                        hideCloseButton,
                        fitContent,
                        bgColorName,
                        edges,
                    },
                ];
            });
        } else {
            // Mark as not visible to trigger close animation
            // The ModalPortal will handle actual removal after animation
            setModalStack((prev) => {
                const existing = prev.find((m) => m.id === modalId);
                if (existing) {
                    return prev.map((m) => (m.id === modalId ? { ...m, visible: false } : m));
                }
                return prev;
            });
        }
    }, [
        visible,
        children,
        finalOnClose,
        hideContainerBackground,
        hideCloseButton,
        fitContent,
        bgColorName,
        edges,
        setModalStack,
    ]);

    // Separate effect for unmount cleanup only
    useEffect(() => {
        const modalId = modalIdRef.current;
        return () => {
            setModalStack((prev) => prev.filter((m) => m.id !== modalId));
        };
    }, [setModalStack]);

    return null;
};

export default Modal;
